import { RefObject, useLayoutEffect, useState } from 'react';

export interface FloatingCardPos {
  top: number;
  left: number;
}

/**
 * Viewport position for a floating detail card, just below the currently
 * focused navigable cell (found by `data-nav-id` within `containerRef`).
 * Returns `null` when nothing is focused.
 *
 * Uses `getBoundingClientRect` + a `position: fixed` card so the card is never
 * clipped by a scrolling/overflow ancestor — letting the menu widgets
 * (ItemList, LoadoutBoard) be dropped anywhere. Shared so the lookup is not
 * re-implemented per screen.
 */
export function useFloatingCardPos(
  containerRef: RefObject<HTMLElement>,
  focusedId: string | null,
): FloatingCardPos | null {
  const [pos, setPos] = useState<FloatingCardPos | null>(null);

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!focusedId || !root) {
      setPos(null);
      return;
    }
    const cell = root.querySelector<HTMLElement>(
      `[data-nav-id="${focusedId}"]`,
    );
    if (!cell) {
      setPos(null);
      return;
    }
    const rect = cell.getBoundingClientRect();
    setPos({ top: rect.bottom + 12, left: rect.left + rect.width / 2 });
  }, [containerRef, focusedId]);

  return pos;
}
